/**
 * useChatSync Hook
 *
 * Syncs messages from Vercel AI SDK's useChat to our Zustand store.
 *
 * CRITICAL: This hook is carefully designed to avoid the "Maximum update depth exceeded"
 * error that occurs when `messages` is in the effect dependency array.
 *
 * Key design decisions:
 * 1. `messages` is stored in a ref, NOT in effect deps (prevents render loops)
 * 2. Sync message IDs immediately when they change (so MessageList can render)
 * 3. Re-sync when streaming finishes so store gets the final message state
 *
 * NOTE: As of AI SDK v6 stable, messages are deep-cloned via structuredClone()
 * on every update (not mutated in place). The store must be re-synced after
 * streaming completes to capture the final message content.
 *
 * @see https://ai-sdk.dev/docs/troubleshooting/react-maximum-update-depth-exceeded
 * @see https://github.com/vercel/ai/issues/4741
 */

import { useEffect, useRef } from 'react'
import { useChatStore } from '../store'
import { logger } from '../logger'
import type { SolaMessage } from '../types'

type ChatStatus = 'idle' | 'streaming' | 'submitted' | 'error' | 'ready'

/**
 * Sync useChat messages to Zustand store
 *
 * CRITICAL: Do NOT put `messages` in effect dependencies!
 * AI SDK creates a new array reference on every throttled chunk,
 * which triggers infinite render loops if used as a dependency.
 */
export function useChatSync(messages: SolaMessage[], status: ChatStatus) {
  const setMessages = useChatStore((s) => s.setMessages)
  const setStatus = useChatStore((s) => s.setStatus)
  const setStreamingMessageId = useChatStore((s) => s.setStreamingMessageId)

  // Store messages in ref - updated after commit but doesn't trigger sync effects
  const messagesRef = useRef(messages)
  useEffect(() => {
    messagesRef.current = messages
  })

  // Track last synced state
  const lastSyncedIds = useRef<string>('')
  const lastSyncedStatus = useRef<ChatStatus>('idle')

  // Compute stable message IDs string (changes only when messages added/removed)
  const currentIds = messages.map(m => m.id).join(',')

  // Sync status changes - depends on status only
  useEffect(() => {
    if (status !== lastSyncedStatus.current) {
      logger.state('Syncing chat status', { from: lastSyncedStatus.current, to: status })

      const previousStatus = lastSyncedStatus.current

      const mappedStatus = status === 'streaming' || status === 'submitted'
        ? 'streaming'
        : status === 'error'
          ? 'error'
          : 'idle'

      setStatus(mappedStatus)
      lastSyncedStatus.current = status

      // Track streaming message ID (read from ref)
      const msgs = messagesRef.current
      if ((status === 'streaming' || status === 'submitted') && msgs.length > 0) {
        setStreamingMessageId(msgs[msgs.length - 1].id)
      } else {
        setStreamingMessageId(null)
      }

      // Re-sync messages when streaming finishes so store gets final cloned state.
      // AI SDK v6 stable uses structuredClone() instead of in-place mutation,
      // so the store's references become stale during streaming.
      const wasStreaming = previousStatus === 'streaming' || previousStatus === 'submitted'
      const isNowDone = status === 'ready' || status === 'idle'
      if (wasStreaming && isNowDone && msgs.length > 0) {
        logger.state('Re-syncing messages after streaming complete', {
          count: msgs.length,
        })
        setMessages(msgs)
        lastSyncedIds.current = msgs.map(m => m.id).join(',')
      }
    }
  }, [status, setStatus, setStreamingMessageId, setMessages])

  // Sync messages ONLY when IDs change (new message added/removed)
  // The key insight: currentIds is a STRING that only changes when message IDs change,
  // NOT on every streaming chunk. So this effect only runs when needed.
  useEffect(() => {
    if (currentIds !== lastSyncedIds.current) {
      const msgs = messagesRef.current
      logger.state('Syncing messages to store (IDs changed)', {
        count: msgs.length,
        previousIds: lastSyncedIds.current,
        currentIds,
      })

      setMessages(msgs)
      lastSyncedIds.current = currentIds
    }
  }, [currentIds, setMessages]) // NO messages in deps - read from ref
}
